// src/components/OnboardingTab.jsx
// Checklist de onboarding de UsuarioDetail, con el timeline vertical
// re-estilizado al patron "chrono board" (DisplayCard): punto de estado
// conectado por una linea, tarjeta resaltada en el paso activo, y un
// pill de estado (Completado / En curso / Pendiente) por paso.
import React, { useState } from 'react';
import { CheckSquare } from 'lucide-react';
import { ChevronDown, ChevronRight } from './icons';
import { DisplayCards } from './ui/DisplayCard';
import api from '../services/api';

const STEPS = [
  {
    id: 'rcp', title: '📩 1. Recepción de Solicitud', items: [
      'Correo de RRHH solicitando creación de usuario y herramientas.',
      'Verificar empresa y área del nuevo colaborador.',
      'Confirmar fecha de ingreso y tipo de contrato.',
    ]
  },
  {
    id: 'cuentas', title: '👤 2. Creación de Cuentas de Usuario', custom: (
      <div className="space-y-3">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">🗂️ AD Brasil</p>
          <ul className="list-disc pl-5 text-sm text-gray-800 dark:text-gray-200 mt-1 space-y-1">
            <li>Crear usuario en OU → LATAM / CHILE / VICSA.</li>
            <li>Formato: nombre.apellido (Vicsa/B2B/Bunzl) · napellido (Tecnoboga).</li>
            <li>Contraseña genérica: Vicsa*2025 (actualizar año).</li>
            <li>Correo con dominio correspondiente.</li>
            <li>proxyAddresses → SMTP:nombre.apellido@empresa.cl</li>
            <li>extensionAttribute1 → E3</li>
            <li>Grupos: Cyberapps_users, Linkedin_users</li>
            <li>Confirmar replicación en O365 Admin Portal</li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">🗂️ AD Vicsa</p>
          <ul className="list-disc pl-5 text-sm text-gray-800 dark:text-gray-200 mt-1 space-y-1">
            <li>Crear usuario en OU del área/empresa.</li>
            <li>Para B2B/Tecnoboga/Bunzl usar carpeta de la empresa.</li>
            <li>Validar login local y de dominio en el equipo asignado.</li>
            <li>Revisar membresías internas según rol (VPN si corresponde).</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    id: 'lic', title: '🪪 3. Asignación de Licencias y Permisos', items: [
      'Asignar licencia Office 365 E3',
      'Grupos de correos masivos según ubicación/empresa',
      'Confirmar acceso a Outlook / Teams / OneDrive',
      'Configurar alias o buzones compartidos (si aplica)',
      'Verificar visibilidad en GAL',
    ]
  },
  {
    id: 'equipo', title: '💻 4. Preparación y Verificación del Equipo', items: [
      'Equipo formateado y configurado',
      'Seguridad corporativa: Rapid7, Cisco, Vicarius',
      'Programas generales, drivers y actualizaciones',
      'Usuario en RICOH',
      'BitLocker activado y clave registrada',
      'Nombre de equipo, dominio y políticas OK',
      'Perfil inicia y OneDrive sincroniza',
    ]
  },
  {
    id: 'valid', title: '🧠 5. Validación Final de Configuración', items: [
      'Test de inicio de sesión',
      'Acceso a carpetas, impresoras y correo',
      'Visibilidad en GAL',
      'Registrar observaciones/incidencias',
    ]
  },
  {
    id: 'acta', title: '📝 6. Documentación y Actas', items: [
      'Generar Acta de Entrega y obtener firma',
      'Guardar copia firmada (HelpDesk / SharePoint)',
    ]
  },
  {
    id: 'cierre', title: '📦 7. Cierre del Proceso', items: [
      'Notificar a RRHH',
      'Guardar comprobantes BitLocker y actas',
      'Actualizar HelpDesk (Usuarios / Activos)',
      'Cerrar checklist con validación TI',
    ]
  },
];

const STEP_ORDER = STEPS.map((s) => s.id);

export default function OnboardingTab({ usuarioId, onboarding, setOnboarding }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const obData = onboarding || { pasos: {}, notas: '' };

  const toggleExpanded = (stepId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  const toggleDone = (stepId) => {
    const checked = !(obData.pasos && obData.pasos[stepId]);
    setOnboarding((p) => {
      const prev = p || { pasos: {}, notas: '' };
      return { ...prev, pasos: { ...prev.pasos, [stepId]: checked } };
    });
  };

  const done = STEP_ORDER.filter((k) => obData.pasos && obData.pasos[k]).length;
  const total = STEP_ORDER.length;
  const percent = Math.round((done / total) * 100);
  const firstPendingIdx = STEP_ORDER.findIndex((k) => !(obData.pasos && obData.pasos[k]));

  const cards = STEPS.map((step, index) => {
    const isDone = !!(obData.pasos && obData.pasos[step.id]);
    const current = !isDone && firstPendingIdx === index;
    const isExpanded = expanded.has(step.id);

    const status = isDone ? 'Completado' : current ? 'En curso' : 'Pendiente';
    const statusClassName = isDone
      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
      : current
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
        : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

    return {
      id: step.id,
      title: step.title,
      description: step.custom ? null : `${step.items.length} puntos a revisar`,
      status,
      statusClassName,
      active: isDone || current,
      onToggle: () => toggleDone(step.id),
      icon: isDone ? <span className="text-xs">✓</span> : undefined,
      actions: (
        <button
          type="button"
          onClick={() => toggleExpanded(step.id)}
          className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {isExpanded ? 'Ocultar detalle' : 'Ver detalle'}
        </button>
      ),
      children: isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          {step.custom ? step.custom : (
            <ul className="list-disc pl-5 text-sm text-gray-800 dark:text-gray-200 space-y-1">
              {step.items.map((it, i) => (<li key={i}>{it}</li>))}
            </ul>
          )}
        </div>
      ),
    };
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <CheckSquare className="h-5 w-5 mr-2 text-green-600" />Checklist de Onboarding
        </h3>
        <button
          onClick={async () => {
            try {
              await api.post('/onboarding/user/save', { usuarioId: Number(usuarioId), dataJson: JSON.stringify(obData) });
              alert('Checklist guardado');
            } catch (e) {
              alert('Error al guardar checklist');
            }
          }}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >Guardar avance</button>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 mb-2">
          <span>Progreso</span>
          <span>{done} de {total} completados ({percent}%)</span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-2 bg-blue-600 rounded-full transition-all" style={{ width: `${percent}%` }} />
        </div>
      </div>

      <DisplayCards cards={cards} />

      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notas</label>
        <textarea
          rows={3}
          value={obData.notas}
          onChange={(e) => {
            const val = e.target.value;
            setOnboarding((p) => {
              const prev = p || { pasos: {}, notas: '' };
              return { ...prev, notas: val };
            });
          }}
          className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Observaciones o pendientes relevantes"
        />
      </div>
    </div>
  );
}
